/**
 * Suggest edits (plan 3.3, 7.4; the WP4 note is the contract):
 * `updatePage` or `updateDocument` in suggest mode return up to 8
 * suggestions `{ target, before, after, reason }` and change nothing. Apply
 * is a manual edit, no model call:
 *
 * - pages: the target is an editor element id (`e12`, the `data-tb`
 *   numbering of `editor-source` at the suggestions' version); the op is
 *   `setText` with the suggested words;
 * - documents: the target is a path, `sections/2/blocks/0` (`/-1` is the
 *   section heading, `/items/1` a list item), `title` or `subtitle`; the
 *   block's words must still read as `before`, so a block that moved or
 *   changed is never overwritten by mistake.
 *
 * A target in any other form (a short quote) cannot be applied here.
 */
import type {
  AgentArtifactKind,
  ArtifactDocument,
  ArtifactDocumentBlock,
  ArtifactSuggestion,
  ArtifactSuggestionsToolResult,
  PageEditOp,
} from '@tabbio/types';
import { escapeInlineText, inlineVisibleText } from '@tabbio/utils/artifact-inline';

export const MAX_SUGGESTIONS = 8;

const KINDS: readonly AgentArtifactKind[] = ['document', 'slides', 'page', 'image', 'file'];

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** The tool result of a suggest-mode call, or null (an applied edit, an error). */
export function parseSuggestionsResult(result: unknown): ArtifactSuggestionsToolResult | null {
  const source = record(result);
  if (!source || source.mode !== 'suggest') return null;
  const artifactId = text(source.artifactId);
  const versionId = text(source.versionId);
  if (!artifactId || !versionId) return null;
  const suggestions: ArtifactSuggestion[] = (Array.isArray(source.suggestions) ? source.suggestions : [])
    .map((item) => record(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      target: text(item.target).trim(),
      before: text(item.before),
      after: text(item.after),
      reason: text(item.reason),
    }))
    .filter((item) => item.target && item.after.trim())
    .slice(0, MAX_SUGGESTIONS);
  return {
    mode: 'suggest',
    artifactId,
    kind: (KINDS as readonly unknown[]).includes(source.kind) ? (source.kind as AgentArtifactKind) : 'page',
    title: text(source.title),
    versionId,
    versionNumber: typeof source.versionNumber === 'number' ? source.versionNumber : 0,
    suggestions,
    message: text(source.message),
  };
}

export type SuggestionTarget =
  | { kind: 'page'; elementId: string }
  | { kind: 'title' }
  | { kind: 'subtitle' }
  | { kind: 'heading'; section: number }
  | { kind: 'block'; section: number; block: number; item: number | null };

const PAGE_TARGET = /^e\d{1,6}$/;
const BLOCK_TARGET = /^\/?sections\/(\d{1,2})\/blocks\/(-1|\d{1,2})(?:\/(?:items|text)(?:\/(\d{1,2}))?)?\/?$/;
const HEADING_TARGET = /^\/?sections\/(\d{1,2})\/heading\/?$/;

/** What a suggestion names, or null when it cannot be applied here. */
export function parseSuggestionTarget(target: string, kind: AgentArtifactKind): SuggestionTarget | null {
  const value = target.trim();
  if (kind === 'page') return PAGE_TARGET.test(value) ? { kind: 'page', elementId: value } : null;
  if (kind !== 'document' && kind !== 'slides') return null;
  if (/^\/?title\/?$/.test(value)) return { kind: 'title' };
  if (/^\/?subtitle\/?$/.test(value)) return { kind: 'subtitle' };
  const heading = HEADING_TARGET.exec(value);
  if (heading) return { kind: 'heading', section: Number(heading[1]) };
  const match = BLOCK_TARGET.exec(value);
  if (!match) return null;
  const section = Number(match[1]);
  const block = Number(match[2]);
  if (block === -1) return { kind: 'heading', section };
  return { kind: 'block', section, block, item: match[3] === undefined ? null : Number(match[3]) };
}

export function isSuggestionApplicable(suggestion: Pick<ArtifactSuggestion, 'target'>, kind: AgentArtifactKind): boolean {
  return parseSuggestionTarget(suggestion.target, kind) !== null;
}

/** A page suggestion as the op Apply sends (the suggested words replace the element's). */
export function pageSuggestionOp(suggestion: Pick<ArtifactSuggestion, 'target' | 'after'>): PageEditOp | null {
  const target = parseSuggestionTarget(suggestion.target, 'page');
  if (target?.kind !== 'page') return null;
  return { op: 'setText', id: target.elementId, text: suggestion.after.trim().slice(0, 4_000) };
}

/** Page targets after a save renumbered the page (null: the element is gone). */
export function remapPageTarget(target: string, ids: Readonly<Record<string, string | null>> | undefined): string | null {
  if (!ids || !Object.prototype.hasOwnProperty.call(ids, target)) return target;
  return ids[target] ?? null;
}

function same(a: string, b: string): boolean {
  const norm = (value: string) => value.replace(/\s+/g, ' ').trim();
  return norm(a) === norm(b);
}

/** A block's visible words (what `before` quotes). */
function visible(document: ArtifactDocument, value: string): string {
  return document.schemaVersion === 2 ? inlineVisibleText(value) : value;
}

export type DocumentApplyResult =
  | { ok: true; document: ArtifactDocument }
  | { ok: false; reason: 'unsupported' | 'stale' };

/**
 * One document suggestion applied to `document` (version 2: the suggested
 * words are escaped, so they read literally). Refused as stale when the
 * target's words no longer read as `before`.
 */
export function applyDocumentSuggestion(
  document: ArtifactDocument,
  suggestion: Pick<ArtifactSuggestion, 'target' | 'before' | 'after'>,
  kind: AgentArtifactKind = 'document',
): DocumentApplyResult {
  const target = parseSuggestionTarget(suggestion.target, kind);
  if (!target || target.kind === 'page') return { ok: false, reason: 'unsupported' };
  const next = JSON.parse(JSON.stringify(document)) as ArtifactDocument;
  next.schemaVersion = 2;
  const v1 = document.schemaVersion !== 2;
  const after = suggestion.after.trim();
  const marked = (value: string) => escapeInlineText(value);
  // A version 1 document is saved as version 2: its other texts are escaped too.
  if (v1) escapeAll(next);
  const checkBefore = (current: string) => !suggestion.before.trim() || same(current, suggestion.before);
  switch (target.kind) {
    case 'title':
      if (!checkBefore(document.title)) return { ok: false, reason: 'stale' };
      next.title = after;
      return { ok: true, document: next };
    case 'subtitle':
      if (!checkBefore(document.subtitle ?? '')) return { ok: false, reason: 'stale' };
      next.subtitle = after;
      return { ok: true, document: next };
    case 'heading': {
      const section = next.sections[target.section];
      if (!section) return { ok: false, reason: 'stale' };
      if (!checkBefore(document.sections[target.section]?.heading ?? '')) return { ok: false, reason: 'stale' };
      section.heading = after;
      return { ok: true, document: next };
    }
    case 'block': {
      const original = document.sections[target.section]?.blocks[target.block];
      const block = next.sections[target.section]?.blocks[target.block];
      if (!original || !block) return { ok: false, reason: 'stale' };
      return applyToBlock(document, original, block, target.item, suggestion.before, after, marked)
        ? { ok: true, document: next }
        : { ok: false, reason: blockSupports(original, target.item) ? 'stale' : 'unsupported' };
    }
  }
}

function blockSupports(block: ArtifactDocumentBlock, item: number | null): boolean {
  if (block.type === 'paragraph' || block.type === 'quote' || block.type === 'callout') return item === null;
  if (block.type === 'bullets' || block.type === 'numbered') return true;
  return false;
}

function applyToBlock(
  document: ArtifactDocument,
  original: ArtifactDocumentBlock,
  block: ArtifactDocumentBlock,
  item: number | null,
  before: string,
  after: string,
  marked: (value: string) => string,
): boolean {
  const matches = (value: string) => !before.trim() || same(visible(document, value), before);
  switch (block.type) {
    case 'paragraph':
    case 'quote':
    case 'callout':
      if (item !== null || (original.type !== block.type) || !('text' in original) || !matches(original.text)) return false;
      block.text = marked(after);
      return true;
    case 'bullets':
    case 'numbered': {
      if (original.type !== 'bullets' && original.type !== 'numbered') return false;
      if (item !== null) {
        const current = original.items[item];
        if (current === undefined || !matches(current)) return false;
        block.items[item] = marked(after);
        return true;
      }
      const whole = original.items.map((value) => visible(document, value));
      if (before.trim() && !same(whole.join('\n'), before) && !same(whole.join(' '), before)) return false;
      const lines = after.split(/\n+/).map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim()).filter(Boolean);
      if (!lines.length) return false;
      block.items = lines.slice(0, 12).map(marked);
      return true;
    }
    default:
      return false;
  }
}

/** Escapes every markable text of a version 1 document (it is saved as version 2). */
function escapeAll(document: ArtifactDocument) {
  for (const section of document.sections) {
    section.blocks = section.blocks.map((block) => {
      switch (block.type) {
        case 'paragraph':
        case 'quote':
        case 'callout':
          return { ...block, text: escapeInlineText(block.text) };
        case 'bullets':
        case 'numbered':
          return { ...block, items: block.items.map(escapeInlineText) };
        case 'table':
          return {
            ...block,
            columns: block.columns.map(escapeInlineText),
            rows: block.rows.map((row) => row.map(escapeInlineText)),
          };
        default:
          return block;
      }
    });
  }
}

/** Several document suggestions in one document (Apply all); the ones that did not apply are listed. */
export function applyDocumentSuggestions(
  document: ArtifactDocument,
  suggestions: readonly Pick<ArtifactSuggestion, 'target' | 'before' | 'after'>[],
  kind: AgentArtifactKind = 'document',
): { document: ArtifactDocument; applied: number[]; refused: { index: number; reason: 'unsupported' | 'stale' }[] } {
  let current = document;
  const applied: number[] = [];
  const refused: { index: number; reason: 'unsupported' | 'stale' }[] = [];
  suggestions.forEach((suggestion, index) => {
    const result = applyDocumentSuggestion(current, suggestion, kind);
    if (result.ok) {
      current = result.document;
      applied.push(index);
    } else refused.push({ index, reason: result.reason });
  });
  return { document: current, applied, refused };
}
